import { BOSS_IDS, type BossId, type RoomMode } from '@bioforge/protocol';
import { BOSS_NAMES } from '../labels.js';
import type { OnlineIntent } from '../scene.js';
import { button, el, overlayRoot, panel } from './html.js';

const CODE_PATTERN = /^[A-Z0-9]{6}$/;

/**
 * Дуэль или кооператив: создать комнату (с боссом или без) или войти по коду.
 * Форма с полем ввода — HTML поверх холста (D-001). Лобби комнаты — `view/lobby-view.ts`.
 */
export class RoomOverlay {
  private readonly root = overlayRoot();
  private readonly onKey = (event: KeyboardEvent): void => {
    event.stopPropagation();
    if (event.key === 'Escape') this.close(null);
  };

  constructor(
    mode: RoomMode,
    private readonly onDone: (intent: OnlineIntent | null) => void,
  ) {
    const box = panel('420px');
    const select = el('select', {
      height: '44px',
      borderRadius: '10px',
      font: '16px Rubik, system-ui, sans-serif',
    });
    const options: [string, string][] = [
      ['', 'Без босса'],
      ...BOSS_IDS.map((id): [string, string] => [id, BOSS_NAMES[id]]),
    ];
    for (const [value, text] of options) {
      const option = el('option', {}, text);
      option.value = value;
      option.selected = value === 'spornitsa';
      select.append(option);
    }
    const bossLabel = el('label', { display: 'flex', flexDirection: 'column', gap: '4px' }, 'Босс');
    bossLabel.append(select);
    const create = button('Создать комнату', '#1b7d74');
    create.addEventListener('click', () => {
      const bossId = select.value as BossId | '';
      this.close({ kind: 'create', mode, ...(bossId ? { bossId } : {}) });
    });

    const code = el('input', {
      height: '44px',
      padding: '0 12px',
      boxSizing: 'border-box',
      borderRadius: '10px',
      border: '2px solid rgba(158,220,240,.45)',
      background: '#0a1826',
      color: '#f2fbff',
      font: '700 18px Unbounded, system-ui, sans-serif',
      letterSpacing: '4px',
      textTransform: 'uppercase',
      flex: '1 1 auto',
      minWidth: '0',
    });
    code.maxLength = 6;
    code.placeholder = 'КОД';
    code.autocomplete = 'off';
    code.spellcheck = false;
    code.setAttribute('aria-label', 'Код комнаты');
    const error = el('p', { margin: '0', color: '#ffb3c8', minHeight: '1.2em' });
    error.setAttribute('role', 'alert');
    const join = button('Войти', '#24506b');
    const submitCode = () => {
      const value = code.value.trim().toUpperCase();
      if (!CODE_PATTERN.test(value)) {
        error.textContent = 'Код комнаты — 6 букв и цифр, например K7M2QX.';
        return;
      }
      this.close({ kind: 'join', code: value });
    };
    join.addEventListener('click', submitCode);
    code.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') submitCode();
    });
    const joinRow = el('div', { display: 'flex', gap: '10px' });
    joinRow.append(code, join);

    const cancel = button('Отмена', '#3a2a4a');
    cancel.addEventListener('click', () => this.close(null));
    box.append(
      el(
        'h2',
        { margin: '0', font: '900 24px Unbounded, system-ui, sans-serif' },
        mode === 'versus' ? 'Дуэль' : 'Кооператив',
      ),
      el(
        'p',
        { margin: '0', color: '#b9d6e3' },
        mode === 'versus'
          ? '1 на 1 по сети: у каждого свой босс.'
          : 'Пилот и Стратег против общего босса.',
      ),
      bossLabel,
      create,
      el('p', { margin: '8px 0 0', color: '#9fc4d6' }, 'Или войдите в комнату друга по коду:'),
      joinRow,
      error,
      cancel,
    );
    this.root.append(box);
    document.body.appendChild(this.root);
    window.addEventListener('keydown', this.onKey, true);
    create.focus();
  }

  private close(intent: OnlineIntent | null): void {
    this.destroy();
    this.onDone(intent);
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey, true);
    this.root.remove();
  }
}

/** Подтверждение действия: заголовок, текст и кнопки. */
export class ConfirmOverlay {
  private readonly root = overlayRoot();
  private readonly onKey = (event: KeyboardEvent): void => {
    event.stopPropagation();
    if (event.key === 'Escape') this.close(false);
  };

  constructor(
    title: string,
    text: string,
    confirm: string,
    private readonly onDone: (confirmed: boolean) => void,
  ) {
    const box = panel('400px');
    const actions = el('div', { display: 'flex', gap: '12px', justifyContent: 'flex-end' });
    const no = button('Отмена', '#24506b');
    no.addEventListener('click', () => this.close(false));
    const yes = button(confirm, '#8a2e5e');
    yes.addEventListener('click', () => this.close(true));
    actions.append(no, yes);
    box.append(
      el('h2', { margin: '0', font: '900 22px Unbounded, system-ui, sans-serif' }, title),
      el('p', { margin: '0', color: '#b9d6e3', lineHeight: '1.4' }, text),
      actions,
    );
    this.root.append(box);
    document.body.appendChild(this.root);
    window.addEventListener('keydown', this.onKey, true);
    no.focus();
  }

  private close(confirmed: boolean): void {
    this.destroy();
    this.onDone(confirmed);
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey, true);
    this.root.remove();
  }
}
